import { useState } from "react";
import { toast } from "react-toastify";
import styles from "./TicketPopup.module.css";

const TicketPopup = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    subject: "",
    description: "",
    priority: "low",
    email: "",
    phoneNumber: "",
    attachedPhoto: null,
    Issues: "",
    otherIssueDetail: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isTicketRaised, setIsTicketRaised] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "Issues" && value !== "other"
        ? { otherIssueDetail: "" }
        : {}),
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith("image/")) {
      setFormData((prev) => ({
        ...prev,
        attachedPhoto: file,
      }));
    } else if (file) {
      alert("Please select an image file only");
      e.target.value = "";
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Basic validation
    if (!formData.subject || !formData.description || !formData.email) {
      alert("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        alert("You must be logged in to raise a ticket");
        return;
      }
      // Create FormData for file upload
      const submitData = new FormData();
      submitData.append("subject", formData.subject);
      submitData.append("description", formData.description);
      submitData.append("priority", formData.priority);
      submitData.append("email", formData.email);
      submitData.append("phoneNumber", formData.phoneNumber);
      submitData.append("Issues", formData.Issues);
      if (formData.Issues === "other" && formData.otherIssueDetail) {
        submitData.append("otherIssueDetail", formData.otherIssueDetail);
      }

      if (formData.attachedPhoto) {
        submitData.append("attachedPhoto", formData.attachedPhoto);
      }

      // Status flags
      submitData.append("isTicketRaised", "true");
      submitData.append("status", "pending");

      // TODO: Replace with your actual API endpoint
      const response = await fetch(
        "https://prod.toxboxai.com/api/tickets/create-ticket",
        {
          method: "POST",
          body: submitData,
          headers: {
            Authorization: `Bearer ${token}`,
          },
          contentType: "multipart/form-data",
        }
      );

      if (response.ok) {
        // Set ticket raised flag to true
        setIsTicketRaised(true);

        toast.success("Ticket raised successfully!");

        // Reset form
        setFormData({
          subject: "",
          description: "",
          priority: "low",
          email: "",
          phoneNumber: "",
          attachedPhoto: null,
        });

        // Reset ticket state after closing
        setTimeout(() => {
          setIsTicketRaised(false);
        }, 1000);

        onClose();
      } else {
        throw new Error("Failed to submit ticket");
      }
    } catch (error) {
      console.error("Error submitting ticket:", error);
      toast.error("Failed to submit ticket. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles.overlay}>
      <div className={styles.popup}>
        <div className={styles.header}>
          <h2>Raise a Ticket</h2>
          <button className={styles.closeBtn} onClick={onClose}>
            ×
          </button>
        </div>

        <form className={styles.form} onSubmit={handleSubmit}>
          <div className={styles.formGroup}>
            <label>
              Ticket Title/Subject <span className={styles.required}>*</span>
            </label>
            <input
              type="text"
              name="subject"
              value={formData.subject}
              onChange={handleInputChange}
              placeholder="Enter ticket subject"
              required
            />
          </div>

          <div className={styles.formGroup}>
            <label>
              Description <span className={styles.required}>*</span>
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Describe your issue in detail"
              rows="4"
              required
            />
          </div>

          <div className={styles.formGroup}>
            <label>Priority</label>
            <select
              name="priority"
              value={formData.priority}
              onChange={handleInputChange}
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
          </div>

          <div className={styles.formGroup}>
            <label>Issues</label>
            <select
              name="Issues"
              value={formData.Issues}
              onChange={handleInputChange}
            >
              <option value="">-- Select an issue --</option>
              <option value="prompt">Prompt issue</option>
              <option value="Technical Issue">Technical issue</option>
              <option value="call issue">call regarding Issues</option>
              <option value="other">Other</option>
            </select>

            {formData.Issues === "other" && (
              <input
                type="text"
                name="otherIssueDetail"
                placeholder="Please describe the issue"
                value={formData.otherIssueDetail || ""}
                onChange={handleInputChange}
                className={styles.otherInput}
              />
            )}
          </div>

          <div className={styles.formGroup}>
            <label>
              Email <span className={styles.required}>*</span>
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="your.email@example.com"
              required
            />
          </div>

          <div className={styles.formGroup}>
            <label>Phone Number</label>
            <input
              type="tel"
              name="phoneNumber"
              value={formData.phoneNumber}
              onChange={handleInputChange}
              placeholder="Enter your phone number"
            />
          </div>

          <div className={styles.formGroup}>
            <label>Attached Photo</label>
            <div className={styles.fileUpload}>
              <input
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                id="photoUpload"
              />
              <label htmlFor="photoUpload" className={styles.fileLabel}>
                {formData.attachedPhoto
                  ? formData.attachedPhoto.name
                  : "Choose image file"}
              </label>
            </div>
          </div>

          <div className={styles.buttonGroup}>
            <button
              type="button"
              className={styles.cancelBtn}
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={styles.submitBtn}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Submitting..." : "Submit Ticket"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TicketPopup;
